import { useState, useMemo, useEffect } from "react";
import { useGoogleAuth } from "@/hooks/useGoogleAuth";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Download, Upload, Plus, Trash2, Edit } from "lucide-react";
import { toast } from "sonner";
import * as XLSX from "xlsx";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { useLocation } from "wouter";

const UFS = ["AC", "AL", "AP", "AM", "BA", "CE", "DF", "ES", "GO", "MA", "MT", "MS", "MG", "PA", "PB", "PR", "PE", "PI", "RJ", "RN", "RS", "RO", "RR", "SC", "SP", "SE", "TO"];

export default function ISS() {
  const { user, loading: authLoading } = useGoogleAuth();
  const [, setLocation] = useLocation();
  
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isBulkDialogOpen, setIsBulkDialogOpen] = useState(false);
  const [isUpdateBulkDialogOpen, setIsUpdateBulkDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [municipioSearchTerm, setMunicipioSearchTerm] = useState("");
  const [selectedMunicipioNome, setSelectedMunicipioNome] = useState("");

  const [formData, setFormData] = useState({
    municipioIbgeId: 0,
    codigoServico: "",
    aliquotaIss: "",
    naturezaOperacaoId: 0,
  });

  const [bulkEditData, setBulkEditData] = useState({
    codigoServico: "",
    aliquotaIss: "",
    naturezaOperacaoId: 0,
  });

  const { data: issList = [], refetch } = trpc.iss.getAll.useQuery();
  const { data: naturezas = [] } = trpc.operationNatures.list.useQuery();
  const { data: emitentes = [] } = trpc.emitentes.list.useQuery();
  const { data: municipios = [] } = trpc.municipiosIbge.search.useQuery(
    { searchTerm: municipioSearchTerm },
    { enabled: municipioSearchTerm.length >= 2 }
  );

  const createMutation = trpc.iss.create.useMutation({
    onSuccess: () => {
      toast.success("ISS cadastrado com sucesso!");
      refetch();
      resetForm();
    },
    onError: (error) => {
      toast.error(`Erro ao cadastrar: ${error.message}`);
    },
  });

  const updateMutation = trpc.iss.update.useMutation({
    onSuccess: () => {
      toast.success("ISS atualizado com sucesso!");
      refetch();
      resetForm();
    },
    onError: (error) => {
      toast.error(`Erro ao atualizar: ${error.message}`);
    },
  });

  const updateBulkMutation = trpc.iss.updateBulk.useMutation({
    onSuccess: () => {
      toast.success("CBS/IBS atualizados em massa!");
      refetch();
      setIsBulkDialogOpen(false);
      setSelectedIds([]);
    },
    onError: (error) => {
      toast.error(`Erro ao atualizar em massa: ${error.message}`);
    },
  });  const deleteMutation = trpc.iss.deleteBatch.useMutation({  onSuccess: () => {
      toast.success("ISS excluídos com sucesso!");
      refetch();
      setSelectedIds([]);
    },
    onError: (error) => {
      toast.error(`Erro ao excluir: ${error.message}`);
    },
  });

  const createBulkMutation = trpc.iss.createBulk.useMutation({
    onSuccess: () => {
      toast.success("ISS importados com sucesso!");
      refetch();
    },
    onError: (error) => {
      toast.error(`Erro ao importar: ${error.message}`);
    },
  });

  const filteredList = useMemo(() => {
    if (!searchTerm) return issList;
    const lower = searchTerm.toLowerCase();
    return issList.filter(
      (item: any) =>
        item.municipioNome?.toLowerCase().includes(lower) ||
        item.codigoServico?.toLowerCase().includes(lower) ||
        item.aliquotaIss?.toString().includes(lower) ||
        item.naturezaNome?.toLowerCase().includes(lower)
    );
  }, [issList, searchTerm]);

  useEffect(() => {
    if (!authLoading && !user) {
      window.location.href = "/login";
    }
  }, [user, authLoading]);

  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Carregando...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return null;
  }


  function resetForm() {
    setFormData({
      municipioIbgeId: 0,
      codigoServico: "",
      aliquotaIss: "",
      naturezaOperacaoId: 0,
    });
    setEditingId(null);
    setIsDialogOpen(false);
    setMunicipioSearchTerm("");
    setSelectedMunicipioNome("");
  }

  function handleOpenDialog(id?: number) {
    if (id) {
      const item = issList.find((c: any) => c.id === id);
      if (item) {
        setFormData({
          municipioIbgeId: item.municipioIbgeId || 0,
          codigoServico: item.codigoServico || "",
          aliquotaIss: item.aliquotaIss || "",
          naturezaOperacaoId: item.naturezaOperacaoId || 0,
        });
        setSelectedMunicipioNome(item.municipioNome || "");
        setEditingId(id);
      }
    }
    setIsDialogOpen(true);
  }

  function handleSubmit() {
    if (!formData.municipioIbgeId || !formData.codigoServico || !formData.aliquotaIss || !formData.naturezaOperacaoId) {
      toast.error("Preencha todos os campos obrigatórios");
      return;
    }

    if (editingId) {
      updateMutation.mutate({ id: editingId, municipioIbgeId: formData.municipioIbgeId, codigoServico: formData.codigoServico, aliquotaIss: parseFloat(formData.aliquotaIss), naturezaOperacaoId: formData.naturezaOperacaoId });
    } else {
      createMutation.mutate({ municipioIbgeId: formData.municipioIbgeId, codigoServico: formData.codigoServico, aliquotaIss: parseFloat(formData.aliquotaIss), naturezaOperacaoId: formData.naturezaOperacaoId });
    }
  }

  function handleBulkEdit() {
    if (selectedIds.length === 0) {
      toast.error("Selecione pelo menos um registro");
      return;
    }
    updateBulkMutation.mutate({ ids: selectedIds, ...bulkEditData });
  }

  function handleDelete() {
    if (selectedIds.length === 0) {
      toast.error("Selecione pelo menos um registro");
      return;
    }
    if (confirm(`Excluir ${selectedIds.length} registro(s)?`)) {
      deleteMutation.mutate({ ids: selectedIds });
    }
  }

  function handleExport() {
    const data = filteredList.map((item: any) => ({
      "Município": item.municipioNome,
      "Código do Serviço": item.codigoServico,
      "Alíquota ISS (%)": item.aliquotaIss,
      "Natureza de Operação": item.naturezaNome,
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "ISS");
    XLSX.writeFile(wb, "iss.xlsx");
    toast.success("Exportado com sucesso!");
  }

  function handleImport(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (evt) => {
      const bstr = evt.target?.result;
      const wb = XLSX.read(bstr, { type: "binary" });
      const wsname = wb.SheetNames[0];
      const ws = wb.Sheets[wsname];
      const data = XLSX.utils.sheet_to_json(ws);

      const items = data.map((row: any) => ({
        municipioIbgeId: parseInt(row["Município ID"]) || 0,
        codigoServico: row["Código do Serviço"] || "",
        aliquotaIss: parseFloat(row["Alíquota ISS (%)"]) || 0,
        naturezaOperacaoId: parseInt(row["Natureza ID"]) || undefined,
      }));

      createBulkMutation.mutate({ items });
    };
    reader.readAsBinaryString(file);
    e.target.value = "";
  }

  function toggleSelectAll() {
    if (selectedIds.length === filteredList.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredList.map((item: any) => item.id));
    }
  }

  function toggleSelect(id: number) {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  }

  return (
    <div className="container mx-auto py-8">
      <div className="mb-6">
        <h1 className="text-3xl font-bold">ISS</h1>
        <p className="text-muted-foreground">Gerencie as alíquotas de ISS por município</p>
      </div>

      <div className="mb-6 flex flex-wrap gap-4 items-center justify-between">
        <div className="flex-1 min-w-[300px]">
          <Label>Buscar</Label>
          <Input
            placeholder="Buscar por município, código do serviço, alíquota ou natureza..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="flex gap-2">
          <Button variant="outline" onClick={handleExport}>
            <Download className="w-4 h-4 mr-2" />
            Exportar
          </Button>
          <Button variant="outline" asChild>
            <label>
              <Upload className="w-4 h-4 mr-2" />
              Importar
              <input type="file" accept=".xlsx,.xls" className="hidden" onChange={handleImport} />
            </label>
          </Button>
          <Button variant="outline" onClick={() => setIsBulkDialogOpen(true)} disabled={selectedIds.length === 0}>
            Alterar em Massa
          </Button>
          <Button variant="outline" onClick={handleDelete} disabled={selectedIds.length === 0}>
            <Trash2 className="w-4 h-4 mr-2" />
            Excluir
          </Button>
          <Button onClick={() => handleOpenDialog()}>
            <Plus className="w-4 h-4 mr-2" />
            Novo ISS
          </Button>
        </div>
      </div>

      <div className="border rounded-lg overflow-hidden">
        <table className="w-full">
          <thead className="bg-muted">
            <tr>
              <th className="p-3 text-left">
                <Checkbox
                  checked={selectedIds.length === filteredList.length && filteredList.length > 0}
                  onCheckedChange={toggleSelectAll}
                />
              </th>
              <th className="p-3 text-left">Município</th>
              <th className="p-3 text-left">Código do Serviço</th>
              <th className="p-3 text-left">Alíquota ISS (%)</th>
              <th className="p-3 text-left">Natureza de Operação</th>
              <th className="p-3 text-left">Emitente</th>
              <th className="p-3 text-left">Ações</th>
            </tr>
          </thead>
          <tbody>
            {filteredList.map((item: any) => (
              <tr key={item.id} className="border-t hover:bg-muted/50">
                <td className="p-3">
                  <Checkbox
                    checked={selectedIds.includes(item.id)}
                    onCheckedChange={() => toggleSelect(item.id)}
                  />
                </td>
                <td className="p-3">{item.municipioNome}</td>
                <td className="p-3">{item.codigoServico}</td>
                <td className="p-3">{item.aliquotaIss}</td>
                <td className="p-3">{(naturezas.find((natureza: any) => natureza.id === item.naturezaOperacaoId) as any)?.nome || item.naturezaOperacaoNome || "-"}</td>
                <td className="p-3">{(emitentes.find((emitente: any) => emitente.id === item.emitenteId) as any)?.razaoSocial || "-"}</td>
                <td className="p-3">
                  <Button variant="ghost" size="sm" onClick={() => handleOpenDialog(item.id)}>
                    <Edit className="w-4 h-4" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Dialog Criar/Editar */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="!w-[80vw] !h-[80vh] !max-w-none overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingId ? "Editar ISS" : "Novo ISS"}</DialogTitle>
          </DialogHeader>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Município Origem *</Label>
              {selectedMunicipioNome ? (
                <div className="flex gap-2">
                  <Input
                    value={selectedMunicipioNome}
                    disabled
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setSelectedMunicipioNome("");
                      setFormData({ ...formData, municipioIbgeId: 0 });
                      setMunicipioSearchTerm("");
                    }}
                  >
                    Limpar
                  </Button>
                </div>
              ) : (
                <>
                  <Input
                    placeholder="Digite para buscar..."
                    value={municipioSearchTerm}
                    onChange={(e) => setMunicipioSearchTerm(e.target.value)}
                  />
                  {municipios.length > 0 && (
                    <div className="border rounded mt-1 max-h-40 overflow-y-auto bg-background">
                      {municipios.map((m: any) => (
                        <div
                          key={m.id}
                          className="p-2 hover:bg-muted cursor-pointer"
                          onClick={() => {
                            setFormData({ ...formData, municipioIbgeId: m.id });
                            setSelectedMunicipioNome(`${m.nome} - ${m.uf}`);
                            setMunicipioSearchTerm("");
                          }}
                        >
                          {m.nome} - {m.uf}
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>

            <div>
              <Label>Código do Serviço *</Label>
              <Input
                value={formData.codigoServico}
                onChange={(e) => setFormData({ ...formData, codigoServico: e.target.value })}
              />
            </div>

            <div>
              <Label>Alíquota ISS (%) *</Label>
              <Input
                type="number"
                step="0.01"
                value={formData.aliquotaIss}
                onChange={(e) => setFormData({ ...formData, aliquotaIss: e.target.value })}
              />
            </div>

            <div>
              <Label>Natureza de Operação *</Label>
              <Select
                value={formData.naturezaOperacaoId?.toString() || "0"}
                onValueChange={(v) => setFormData({ ...formData, naturezaOperacaoId: parseInt(v) })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione" />
                </SelectTrigger>
                <SelectContent>
                  {naturezas.map((n: any) => (
                    <SelectItem key={n.id} value={n.id.toString()}>{n.nome}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={resetForm}>Cancelar</Button>
            <Button onClick={handleSubmit}>Salvar</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Alterar em Massa */}
      <Dialog open={isBulkDialogOpen} onOpenChange={setIsBulkDialogOpen}>
        <DialogContent className="!w-[80vw] !h-[80vh] !max-w-none overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Alterar em Massa ({selectedIds.length} selecionados)</DialogTitle>
          </DialogHeader>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Código do Serviço</Label>
              <Input
                value={bulkEditData.codigoServico}
                onChange={(e) => setBulkEditData({ ...bulkEditData, codigoServico: e.target.value })}
              />
            </div>

            <div>
              <Label>Alíquota ISS (%)</Label>
              <Input
                type="number"
                step="0.01"
                value={bulkEditData.aliquotaIss}
                onChange={(e) => setBulkEditData({ ...bulkEditData, aliquotaIss: e.target.value })}
              />
            </div>

            <div>
              <Label>Natureza de Operação</Label>
              <Select
                value={bulkEditData.naturezaOperacaoId?.toString() || "0"}
                onValueChange={(v) => setBulkEditData({ ...bulkEditData, naturezaOperacaoId: parseInt(v) })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione" />
                </SelectTrigger>
                <SelectContent>
                  {naturezas.map((n: any) => (
                    <SelectItem key={n.id} value={n.id.toString()}>{n.nome}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setIsBulkDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleBulkEdit}>Aplicar</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
